import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  PARTNER_LEVEL_LABELS,
  PARTNER_STATUS_LABELS,
  PARTNER_TYPE_FORM_OPTIONS,
} from "../constants/partnerEnums";
import { toSelectOptions } from "../constants/commissionEnums";
import {
  normalizeCanCreateSubPartners,
  normalizeMobile,
  normalizeReferralCodeInput,
  validatePartnerForm,
  validateReferralCode,
} from "../utils/partnerValidation";

const emptyForm = () => ({
  mobile: "",
  firstName: "",
  lastName: "",
  initialPassword: "",
  userId: "",
  parentPartnerId: "",
  partnerLevel: "Level1",
  partnerType: "ProfessionalPartner",
  status: "PendingActivation",
  commissionGroupId: "",
  referralCode: "",
  canCreateSubPartners: false,
});

const PartnerFormDialog = ({
  open,
  onClose,
  onSubmit,
  initialData,
  groups,
  level1Partners,
  saving,
}) => {
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(initialData?.id);

  const activeGroups = useMemo(
    () => groups.filter((g) => g.isActive !== false),
    [groups]
  );

  const canToggleSubPartners =
    form.partnerLevel === "Level1" && form.partnerType !== "PublicReferrer";

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setErrors({});
    if (initialData) {
      setForm({
        mobile: initialData.mobileNumber ?? "",
        firstName: initialData.displayName?.split(" ")?.[0] ?? "",
        lastName: initialData.displayName?.split(" ")?.slice(1).join(" ") ?? "",
        initialPassword: "",
        userId: initialData.userId ?? "",
        parentPartnerId: initialData.parentPartnerId ?? "",
        partnerLevel: initialData.partnerLevel || "Level1",
        partnerType: initialData.partnerType || "ProfessionalPartner",
        status: initialData.status || "PendingActivation",
        commissionGroupId: initialData.commissionGroupId ?? "",
        referralCode: initialData.referralCode || "",
        canCreateSubPartners: initialData.canCreateSubPartners ?? false,
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, initialData]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "partnerLevel") {
        if (value === "Level2") {
          next.canCreateSubPartners = false;
        }
        if (value === "Level1") {
          next.parentPartnerId = "";
        }
      }
      if (field === "partnerType" && value === "PublicReferrer") {
        next.canCreateSubPartners = false;
      }
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async () => {
    const validationErrors = validatePartnerForm(form, { isEdit });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      parentPartnerId: form.parentPartnerId ? Number(form.parentPartnerId) : null,
      partnerLevel: form.partnerLevel,
      partnerType: form.partnerType,
      status: form.status,
      commissionGroupId: Number(form.commissionGroupId),
      referralCode: normalizeReferralCodeInput(form.referralCode) || null,
      canCreateSubPartners: normalizeCanCreateSubPartners(form),
    };

    if (isEdit) {
      payload.userId = Number(form.userId);
    } else {
      payload.mobile = normalizeMobile(form.mobile);
      payload.firstName = form.firstName?.trim() || null;
      payload.lastName = form.lastName?.trim() || null;
      payload.initialPassword = form.initialPassword?.trim() || null;
    }

    const result = await onSubmit(payload);
    if (result?.error) setSubmitError(result.error);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "ویرایش نماینده" : "ایجاد نماینده جدید"}</DialogTitle>
      <DialogContent dividers>
        {submitError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          {!isEdit ? (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="شماره موبایل"
                  value={form.mobile}
                  onChange={(e) => handleChange("mobile", e.target.value)}
                  fullWidth
                  required
                  error={Boolean(errors.mobile)}
                  helperText={
                    errors.mobile ||
                    "اگر اکانت نداشته باشد، به‌صورت خودکار ساخته می‌شود."
                  }
                  inputProps={{ inputMode: "numeric" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="رمز عبور اولیه"
                  value={form.initialPassword}
                  onChange={(e) => handleChange("initialPassword", e.target.value)}
                  fullWidth
                  helperText="اختیاری — پیش‌فرض: 123456"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="نام"
                  value={form.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="نام خانوادگی"
                  value={form.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  fullWidth
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12} sm={6}>
              <TextField
                label="موبایل / شناسه کاربر"
                value={form.mobile || form.userId}
                fullWidth
                disabled
                helperText={`UserId: ${form.userId}`}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={Boolean(errors.partnerLevel)}>
              <InputLabel>سطح نماینده</InputLabel>
              <Select
                value={form.partnerLevel}
                label="سطح نماینده"
                onChange={(e) => handleChange("partnerLevel", e.target.value)}
              >
                {toSelectOptions(PARTNER_LEVEL_LABELS).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={Boolean(errors.partnerType)}>
              <InputLabel>نوع نماینده</InputLabel>
              <Select
                value={form.partnerType}
                label="نوع نماینده"
                onChange={(e) => handleChange("partnerType", e.target.value)}
              >
                {toSelectOptions(PARTNER_TYPE_FORM_OPTIONS).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={Boolean(errors.status)}>
              <InputLabel>وضعیت</InputLabel>
              <Select
                value={form.status}
                label="وضعیت"
                onChange={(e) => handleChange("status", e.target.value)}
              >
                {toSelectOptions(PARTNER_STATUS_LABELS).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={Boolean(errors.commissionGroupId)}>
              <InputLabel>گروه پورسانتی</InputLabel>
              <Select
                value={form.commissionGroupId}
                label="گروه پورسانتی"
                onChange={(e) => handleChange("commissionGroupId", e.target.value)}
              >
                {activeGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.title} ({group.code})
                  </MenuItem>
                ))}
              </Select>
              {errors.commissionGroupId ? (
                <Typography variant="caption" color="error">
                  {errors.commissionGroupId}
                </Typography>
              ) : null}
            </FormControl>
          </Grid>
          {form.partnerLevel === "Level2" ? (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={Boolean(errors.parentPartnerId)}>
                <InputLabel>نماینده والد (سطح ۱)</InputLabel>
                <Select
                  value={form.parentPartnerId}
                  label="نماینده والد (سطح ۱)"
                  onChange={(e) => handleChange("parentPartnerId", e.target.value)}
                >
                  {level1Partners.map((partner) => (
                    <MenuItem key={partner.id} value={partner.id}>
                      #{partner.id} — کاربر {partner.userId} ({partner.referralCode})
                    </MenuItem>
                  ))}
                </Select>
                {errors.parentPartnerId ? (
                  <Typography variant="caption" color="error">
                    {errors.parentPartnerId}
                  </Typography>
                ) : null}
              </FormControl>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6}>
            <TextField
              label="کد معرف"
              value={form.referralCode}
              onChange={(e) => handleChange("referralCode", e.target.value)}
              fullWidth
              placeholder="مثلاً 554"
              error={Boolean(errors.referralCode)}
              helperText={
                errors.referralCode ||
                "در صورت خالی بودن، سیستم یک کد کوتاه یکتا تولید می‌کند."
              }
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.canCreateSubPartners}
                  onChange={(e) => handleChange("canCreateSubPartners", e.target.checked)}
                  disabled={!canToggleSubPartners}
                />
              }
              label="اجازه ساخت زیرمجموعه"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          انصراف
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "در حال ذخیره..." : isEdit ? "ذخیره" : "ایجاد"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerFormDialog;
